import { FormattedMessage, useIntl } from 'react-intl'
import { useParams } from 'react-router-dom/cjs/react-router-dom'
import { collectWinning, useTPKState } from 'shared/store/tu-preferes-king'
import { Button, colors, Spacer } from '../../../app-v2/design-systems'
import { SIZE, SPACING } from '../../../app-v2/design-tokens'
import React, { useState } from 'react'
import { InfoKeepCard } from '../components/InfoKeepCard'
import { useHistory } from 'react-router-dom'
import { CaptionText, H4, Text } from '../../common/typography'
import { Form, Formik } from 'formik'
import { CheckOrRadio, Checkbox, Input } from '../../common/forms'
import { Spinner } from '../../assets/icons'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState } from 'shared/store/reducers'
import { TPKColors } from '../colors'
import { bool, object, string } from 'yup'
import { regex } from 'shared/utils'
import { useWindowDimensions } from '../utils'
import styled from 'styled-components'
import { TPKLayout } from '../components/TPKLayout'
import { BORDER } from 'app-v2/design-tokens'
import { media } from '../../assets/styles'

const validationSchema = () => {
    const mandatory = 'form.error.field.mandatory'
    return object().shape({
        email: string()
            .required(mandatory)
            .matches(regex.EMAIL, {
                message: 'form.error.regex.email',
                excludeEmptyString: true
            }),
        oldEnough: bool().oneOf([true], mandatory),
        readRules: bool().oneOf([true], mandatory)
    })
}

export const TuPreferesKingEmailForm = () => {
    const intl = useIntl()
    const history = useHistory()
    const { tpkToken } = useParams()
    const data = useTPKState(tpkToken, { autoload: true })
    const { width: windowWidth } = useWindowDimensions()
    const dispatch = useDispatch()

    const { authenticated } = useSelector((state: RootState) => {
        return {
            authenticated: state.userState.authenticated,
            email: state.userState.authenticated
                ? state.kingdomProfilePageState.email ||
                  state.signinPageState.login
                : ''
        }
    })
    const [submitError, setSubmitError] = useState('')

    const redirectToQrCode = () => {
        history.replace(
            intl.formatMessage(
                { id: 'routes.tupreferesking.qrCode.tpkToken' },
                {
                    tpkToken
                }
            )
        )
    }

    if (data.duel && data.duel.email && data.duel.couponCode) {
        redirectToQrCode()
    }

    return (
        <TPKLayout
            header={{ withBuoy: true, withSurf: true, mediumBackground: true }}>
            <StyledCard>
                <H4>
                    {intl.formatMessage({
                        id: 'tupreferesking.email.title'
                    })}
                </H4>
                <StyledTitleContainer>
                    <Text center>
                        {intl.formatMessage({
                            id: 'tupreferesking.email.description'
                        })}
                    </Text>
                </StyledTitleContainer>

                <Formik
                    initialValues={{
                        email: '',
                        optin: false,
                        readRules: false,
                        oldEnough: false
                    }}
                    onSubmit={(form, { setSubmitting }) => {
                        dispatch(
                            collectWinning(tpkToken, form.email, form.optin)
                        )
                            .then(() => redirectToQrCode())
                            .catch(e => {
                                setSubmitError(e.message)
                            })
                            .finally(() => {
                                setSubmitting(false)
                            })
                    }}
                    validateOnMount={true}
                    enableReinitialize={true}
                    validationSchema={validationSchema}>
                    {({ errors, values, handleChange, isSubmitting }) => {
                        const canSubmit =
                            !errors.email &&
                            !!values.email &&
                            !errors.oldEnough &&
                            !errors.readRules &&
                            !isSubmitting
                        return (
                            <Form disabled={isSubmitting}>
                                {submitError ? (
                                    <Text color={colors.fieryRed}>
                                        {intl.formatMessage({
                                            id: `tupreferesking.errors.${submitError}`
                                        })}
                                    </Text>
                                ) : null}
                                <StyledForm>
                                    <Input
                                        className={'tpkModal'}
                                        name={'email'}
                                        type="email"
                                        placeholderKey="tupreferesking.email.placeholder"
                                    />
                                    {!authenticated && (
                                        <CheckOrRadio
                                            name="optin"
                                            type="checkbox"
                                            labelKey="tupreferesking.email.optin">
                                            <StyledCheckbox
                                                handleChange={handleChange}
                                                checkedColor={colors.bbqBrown}
                                                name={'optin'}
                                                checked={values.optin}
                                                color={colors.bbqBrown}
                                            />
                                        </CheckOrRadio>
                                    )}

                                    <CheckOrRadio
                                        name="readRules"
                                        type="checkbox"
                                        label={
                                            <FormattedMessage
                                                id={
                                                    'tupreferesking.email.readRules'
                                                }
                                                values={{
                                                    aRules: chunk => (
                                                        <StyledLink
                                                            target={'_blank'}
                                                            href={intl.formatMessage(
                                                                {
                                                                    id:
                                                                        'routes.tupreferesking.legals'
                                                                }
                                                            )}>
                                                            {chunk}
                                                        </StyledLink>
                                                    )
                                                }}
                                            />
                                        }>
                                        <StyledCheckbox
                                            handleChange={handleChange}
                                            checkedColor={colors.bbqBrown}
                                            name={'readRules'}
                                            checked={values.readRules}
                                            color={colors.bbqBrown}
                                        />
                                    </CheckOrRadio>

                                    <CheckOrRadio
                                        name="oldEnough"
                                        type="checkbox"
                                        labelKey="tupreferesking.email.oldEnough">
                                        <StyledCheckbox
                                            handleChange={handleChange}
                                            checkedColor={colors.bbqBrown}
                                            name={'oldEnough'}
                                            checked={values.oldEnough}
                                            color={colors.bbqBrown}
                                        />
                                    </CheckOrRadio>

                                    <StyledButtonContainer>
                                        <Button
                                            fullLength
                                            buttonColor={
                                                canSubmit
                                                    ? colors.crunchyGreen
                                                    : TPKColors.disableBackground
                                            }
                                            labelColor={
                                                canSubmit
                                                    ? colors.white
                                                    : TPKColors.disableText
                                            }
                                            disabled={!canSubmit}
                                            type={'submit'}
                                            otherStyle={{
                                                padding: `${SPACING.XXS} ${SPACING.S}`,
                                                display: 'flex',
                                                flexDirection: 'row',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                alignContent: 'center',
                                                minHeight: 24
                                            }}>
                                            {intl.formatMessage({
                                                id:
                                                    'tupreferesking.email.validate'
                                            })}
                                            {isSubmitting && (
                                                <Spinner
                                                    size={18}
                                                    color={
                                                        TPKColors.disableText
                                                    }
                                                />
                                            )}
                                        </Button>
                                    </StyledButtonContainer>

                                    <CaptionText>
                                        {intl.formatMessage({
                                            id: 'tupreferesking.mandatory'
                                        })}
                                    </CaptionText>
                                </StyledForm>
                            </Form>
                        )
                    }}
                </Formik>
            </StyledCard>

            <Spacer size={SIZE.XXL} />

            <InfoKeepCard />
        </TPKLayout>
    )
}

const StyledCheckbox = styled(Checkbox)`
    flex: none;
`
const StyledLink = styled.a`
    text-decoration: underline;
    &:hover,
    &:active {
        text-decoration: underline;
    }
`

const StyledButtonContainer = styled.div`
    width: 100%;
    margin-top: ${SPACING.S};
    display: flex;
    gap: ${SPACING.XXXS};
    justify-content: center;
`

const StyledForm = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`

const StyledCard = styled.div`
    margin-top: -150px;
    box-sizing: border-box;
    z-index: 10;
    position: relative;
    padding: ${SPACING.XL} ${SPACING.M} ${SPACING.M};
    width: 100%;
    background: ${colors.white};
    border-radius: ${BORDER.RADIUS.L};
    display: flex;
    border-top: 20px solid ${TPKColors.borderCard};
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${SPACING.S};
    ${media.small`
    gap: ${SPACING.L};
    padding: 42px 68px;
    `}
`

const StyledTitleContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${SPACING.XXXS};
    padding: ${SPACING.NONE} ${SPACING.S};
    max-width: 340px;
`
